import React from 'react';
import { Divider, Grid, Typography } from '@mui/material';

import { ParagonColors } from '@paragon/client-tools';
import { hasNewLayoutModeFeatureFlag } from '@paragon/login-tools';
import { Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch, isMac, KeyCombination } from '@paragon/utilities';

import { DEFAULT_KEYBINDINGS, HotKeyActionType } from '../../store/selectors/keyboardSelectors';
import { useAppSelector } from '../hooks';

export function Hotkeys() {
  const hasLayoutPermission = useAppSelector(hasNewLayoutModeFeatureFlag);
  return (
    <Dialog
      variant="design"
      title="Hotkey Reference"
      submitText="Done"
      hasTransition={false}
      contentSx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Grid container spacing={0.5}>
        {Object.keys(DEFAULT_KEYBINDINGS)
          .filter(isHotkeyVisible(hasLayoutPermission))
          .toSorted(hotkeySort)
          .map((action, index) => (
            <Hotkey key={action} action={action} first={!index} />
          ))}
      </Grid>
    </Dialog>
  );
}

function Hotkey(props: { action: HotKeyActionType; first: boolean }) {
  const actionData = DIALOG_KEYCOMBINATION_OVERRIDES[props.action] ?? DEFAULT_KEYBINDINGS[props.action];
  if (!actionData) return null;

  return (
    <>
      {!props.first && (
        <Grid size={12}>
          <Divider />
        </Grid>
      )}
      {actionData.map((keyCombination, index) => (
        <React.Fragment key={index}>
          <Grid size={7}>{index === 0 ? <Typography>{hotkeyActionDisplayName(props.action)}</Typography> : null}</Grid>
          <Grid size={5}>
            <KeyCombinationText keyCombination={keyCombination} />
          </Grid>
        </React.Fragment>
      ))}
    </>
  );
}

function KeyCombinationText(params: { keyCombination: KeyCombination }) {
  const { keyCode, modifierKeys, allowMacCtrlKey } = params.keyCombination;

  const modifierKey = (name: string, macName: string, active: boolean | undefined) =>
    active ? <CodeText>{isMac() ? macName : name}</CodeText> : null;

  return (
    <>
      {modifierKey('Ctrl', allowMacCtrlKey ? 'Command / Ctrl' : 'Command', modifierKeys.ctrlKey)}
      {modifierKey('Alt', 'Option', modifierKeys.altKey)}
      {modifierKey('Shift', 'Shift', modifierKeys.shiftKey)}
      <CodeText>{keyCodeToKeyName(keyCode)}</CodeText>
    </>
  );
}

function CodeText({ children }: { children: string }) {
  return (
    <code
      style={{
        margin: 1,
        padding: '2px 4px',
        backgroundColor: ParagonColors.canvasDark,
        border: '1px solid',
        borderColor: ParagonColors.green,
        borderRadius: 6,
        marginLeft: 'auto',
      }}
    >
      {children}
    </code>
  );
}

const DIALOG_KEYCOMBINATION_OVERRIDES: { [K in HotKeyActionType]?: readonly KeyCombination[] } = {
  cuttingMode: [{ keyCode: 'Shift', modifierKeys: {} }],

  incrementBasicInsertionPlurality: [{ keyCode: '+', modifierKeys: {} }],
  decrementBasicInsertionPlurality: [{ keyCode: '-', modifierKeys: {} }],
};

function isHotkeyVisible(hasLayoutPermission: boolean) {
  return (action: string): action is HotKeyActionType => {
    const hotkeyAction = action as HotKeyActionType;
    switch (hotkeyAction) {
      case 'cycleTrussEnvelopeRenderMode':
      case 'designEnvelopes':
      case 'designNewEnvelope':
      case 'designView':
      case 'duplicate':
      case 'layoutView':
      case 'move':
      case 'toggle3dView':
      case 'selectEnvelopeDesign':
      case 'toggleLockToBase':
        return hasLayoutPermission;

      case 'cuttingMode':
      case 'decrementBasicInsertionPlurality':
      case 'delete':
      case 'download':
      case 'escape':
      case 'find':
      case 'focusCamera':
      case 'hotkeys':
      case 'incrementBasicInsertionPlurality':
      case 'insert':
      case 'moveToNextComponent':
      case 'redo':
      case 'runParagonProcess':
      case 'showChooseProjectDialog':
      case 'toggleDimensionMode':
      case 'toggleLeftSidebar':
      case 'toggleReferenceLines':
      case 'toggleRightSidebar':
      case 'undo':
        return true;

      default:
        assertExhaustiveSwitch(hotkeyAction);
    }
  };
}

function hotkeyActionDisplayName(hotkeyAction: HotKeyActionType): string {
  switch (hotkeyAction) {
    case 'cuttingMode':
      return 'Cut member/Trim envelope';
    case 'cycleTrussEnvelopeRenderMode':
      return 'Cycle truss envelope render mode';
    case 'decrementBasicInsertionPlurality':
      return 'Decrease inserted line count';
    case 'delete':
      return 'Delete selected item';
    case 'designEnvelopes':
      return 'Design/Regenerate Components';
    case 'designNewEnvelope':
      return 'Create New Design';
    case 'designView':
      return 'Design view';
    case 'download':
      return 'Download files';
    case 'duplicate':
      return 'Duplicate selected items';
    case 'escape':
      return 'Exit/escape';
    case 'find':
      return 'Find component';
    case 'focusCamera':
      return 'Focus camera on item';
    case 'hotkeys':
      return 'Show/hide hotkey reference';
    case 'incrementBasicInsertionPlurality':
      return 'Increase inserted line count';
    case 'insert':
      return 'Insert item';
    case 'layoutView':
      return 'Layout view';
    case 'move':
      return 'Move selected items';
    case 'moveToNextComponent':
      return 'Next component';
    case 'redo':
      return 'Redo';
    case 'runParagonProcess':
      return 'Analyze';
    case 'selectEnvelopeDesign':
      return 'Choose Existing Design/Unlink Components';
    case 'showChooseProjectDialog':
      return 'Choose project';
    case 'toggle3dView':
      return 'Toggle 3D mode';
    case 'toggleDimensionMode':
      return 'Toggle dimension mode';
    case 'toggleLeftSidebar':
      return 'Toggle left sidebar';
    case 'toggleLockToBase':
      return 'Lock envelope base';
    case 'toggleReferenceLines':
      return 'Show/hide reference lines';
    case 'toggleRightSidebar':
      return 'Toggle right sidebar';
    case 'undo':
      return 'Undo';

    default:
      assertExhaustiveSwitch(hotkeyAction);
  }
}

function hotkeySort(hotkeyAction: HotKeyActionType, otherAction: HotKeyActionType): number {
  const ordering: Record<HotKeyActionType, true> = {
    runParagonProcess: true,

    download: true,

    showChooseProjectDialog: true,

    escape: true,
    delete: true,

    find: true,
    insert: true,
    move: true,
    duplicate: true,

    undo: true,
    redo: true,

    layoutView: true,
    designView: true,
    toggle3dView: true,
    cycleTrussEnvelopeRenderMode: true,
    focusCamera: true,

    designEnvelopes: true,
    selectEnvelopeDesign: true,
    designNewEnvelope: true,

    moveToNextComponent: true,

    toggleDimensionMode: true,
    toggleReferenceLines: true,

    incrementBasicInsertionPlurality: true,
    decrementBasicInsertionPlurality: true,

    cuttingMode: true,

    toggleLeftSidebar: true,
    toggleRightSidebar: true,

    toggleLockToBase: true,

    hotkeys: true,
  };

  const index = (type: HotKeyActionType) => Object.keys(ordering).indexOf(type);

  return index(hotkeyAction) - index(otherAction);
}

function keyCodeToKeyName(keyCode: string): string {
  if (keyCode.length === 4 && keyCode.startsWith('Key')) return keyCode.charAt(3);
  if (keyCode.length === 6 && keyCode.startsWith('Digit')) return keyCode.charAt(5);
  if (keyCode.startsWith('Arrow')) return keyCode.substring(5);

  return (
    {
      Comma: ',',
      Period: '.',
      Slash: '/',
    }[keyCode] ?? keyCode
  );
}
